// store/forms/photosForm/photosFormActions.js
// Actions for the reusable photos module - can be integrated into any form

import { FormPhotos } from "../../../models/FormPhotos/FormPhotos";
import { extractGeoFromExif } from "../../../services/exifService";
import {
  uploadFileToS3,
  deleteFileFromS3,
  downloadFileFromS3,
} from "../../../api/PhotosApi";

/**
 * Creates actions for the photos module.
 * @param {object} api - Object containing helpers:
 *   - getFormData: () => formInstance - Get current form data (e.g., currentDel)
 *   - setPhotosUi: (patch) => void - Update photos UI state
 *   - getPhotosUi: () => photosUI - Get current photos UI state
 *   - bump: () => void - Trigger re-render (optional)
 * @returns {object} Frozen object containing photo actions
 */
export const makePhotosActions = (api) => ({
  // ============================================
  // LOCAL STATE ACTIONS (no server calls)
  // ============================================

  /**
   * Add files to pending upload selection
   * @param {FileList|File[]} files - Files from input element
   */
  addPendingPhotos: async (files) => {
    const photosUi = api.getPhotosUi();
    const existingPhotos = photosUi.selectedPhotos;
    const newPhotos = await Promise.all(
      Array.from(files).map(async (file) => {
        const photo = await FormPhotos.fromLocalFile(file);
        // Extract EXIF geo-metadata from gallery uploads
        const geoData = await extractGeoFromExif(file);
        if (geoData) {
          photo.latitude = geoData.latitude;
          photo.longitude = geoData.longitude;
          photo.altitude = geoData.altitude;
          photo.bearing = geoData.bearing;
          photo.accuracy = geoData.accuracy;
          photo.capturedAt = geoData.capturedAt;
          photo.capturedAtLocal = geoData.capturedAtLocal;
        }
        return photo;
      }),
    );
    api.setPhotosUi({
      selectedPhotos: [...existingPhotos, ...newPhotos],
      uploadError: null,
    });
  },

  /**
   * Add photos from camera capture (already have geo data, no EXIF needed)
   * @param {FormPhotos[]} photos - FormPhotos instances from CameraModal
   */
  addPendingPhotosWithGeo: (photos) => {
    const photosUi = api.getPhotosUi();
    const existingPhotos = photosUi.selectedPhotos;
    api.setPhotosUi({
      selectedPhotos: [...existingPhotos, ...photos],
      uploadError: null,
    });
  },

  /**
   * Remove a photo from pending selection (before upload)
   * @param {string} photoId - UUID of the photo to remove
   */
  removePendingPhoto: (photoId) => {
    const photosUi = api.getPhotosUi();
    const selectedPhotos = photosUi.selectedPhotos;
    const photoToRemove = selectedPhotos.find((p) => p.id === photoId);

    // Revoke blob URL to prevent memory leaks
    if (photoToRemove) {
      photoToRemove.revokeLocalPreview();
    }

    api.setPhotosUi({
      selectedPhotos: selectedPhotos.filter((p) => p.id !== photoId),
    });
  },

  /**
   * Update caption on a pending photo (local only)
   * @param {string} photoId - UUID of the photo
   * @param {string} caption - New caption text
   */
  updatePendingPhotoCaption: (photoId, caption) => {
    const photosUi = api.getPhotosUi();
    const selectedPhotos = photosUi.selectedPhotos;
    const updatedPhotos = selectedPhotos.map((photo) => {
      if (photo.id === photoId) {
        photo.updateCaption(caption);
      }
      return photo;
    });
    api.setPhotosUi({ selectedPhotos: updatedPhotos });
  },

  /**
   * Clear all pending photos (after successful upload or cancel)
   */
  clearPendingPhotos: () => {
    const photosUi = api.getPhotosUi();
    const selectedPhotos = photosUi.selectedPhotos;
    // Revoke all blob URLs
    selectedPhotos.forEach((photo) => photo.revokeLocalPreview());
    api.setPhotosUi({ selectedPhotos: [] });
  },

  // ============================================
  // SERVER OPERATIONS
  // ============================================

  /**
   * Upload all pending photos to S3 and save metadata
   * @param {function} emitPatch - WebSocket emit function (returns {success, error, data})
   * @param {string} formId - Parent form UUID (del_id, safety_audit_id, etc.)
   * @param {string} formType - Type of form ("del", "safety_audit", etc.)
   * @returns {Promise<{ok: boolean, error?: string}>}
   */
  uploadPendingPhotos: async (emitPatch, formId, formType) => {
    const photosUi = api.getPhotosUi();
    const pendingPhotos = photosUi.selectedPhotos;

    if (pendingPhotos.length === 0) {
      return { ok: true };
    }

    api.setPhotosUi({ isUploading: true, uploadError: null });

    // Step 1: Request presigned URLs
    const filesInfo = pendingPhotos.map((photo) => ({
      id: photo.id,
      file_type: photo.mimeType,
      size: photo.size,
      width: photo.width,
      height: photo.height,
    }));

    const presignResult = await emitPatch({
      patch_type: "photos",
      [`${formType}_id`]: formId,
      photo_data: {
        patch_type: "photos_presign",
        files: filesInfo,
      },
    });

    if (!presignResult.success) {
      api.setPhotosUi({
        isUploading: false,
        uploadError: presignResult.error || "Failed to get upload URLs",
      });
      return {
        ok: false,
        error: presignResult.error || "Failed to get upload URLs",
      };
    }

    // Get presigned_urls from the response data
    const presignedUrls = presignResult.data?.presigned_urls;
    if (!presignedUrls || presignedUrls.length === 0) {
      api.setPhotosUi({
        isUploading: false,
        uploadError: "No presigned URLs returned from server",
      });
      return { ok: false, error: "No presigned URLs returned from server" };
    }

    // Step 2: Upload each file to S3
    const uploadedPhotos = [];
    for (const photo of pendingPhotos) {
      const presignData = presignedUrls.find((p) => p.id === photo.id);
      if (!presignData) continue;

      // Upload to S3 using PhotosApi
      const uploadResult = await uploadFileToS3(
        presignData.presigned_url,
        photo.localFile,
        photo.mimeType
      );

      if (!uploadResult.ok) {
        api.setPhotosUi({
          isUploading: false,
          uploadError: uploadResult.error || `Failed to upload photo`,
        });
        return { ok: false, error: uploadResult.error };
      }

      // Update photo with S3 key
      photo.updateS3Key(presignData.s3_key);
      photo.updateUploadStatus("uploaded");
      uploadedPhotos.push(photo);
    }

    // Step 3: Save metadata for each uploaded photo
    for (const photo of uploadedPhotos) {
      const metadataResult = await emitPatch({
        patch_type: "photos",
        [`${formType}_id`]: formId,
        photo_data: photo.toAddPatch(),
      });

      if (!metadataResult.success) {
        api.setPhotosUi({
          isUploading: false,
          uploadError:
            metadataResult.error || "Failed to save photo metadata",
        });
        return {
          ok: false,
          error: metadataResult.error || "Failed to save photo metadata",
        };
      }

      // Update photo with presigned GET URL from server response for display
      if (metadataResult.data?.presigned_url) {
        photo.updatePresignedUrl(metadataResult.data.presigned_url);
      }
    }

    // Step 4: Add uploaded photos to form data and clear pending
    const formData = api.getFormData();
    const existingPhotos = formData.photos || [];
    formData.photos = [...existingPhotos, ...uploadedPhotos];

    // Revoke blob URLs and clear pending
    pendingPhotos.forEach((photo) => photo.revokeLocalPreview());
    api.setPhotosUi({
      selectedPhotos: [],
      isUploading: false,
      uploadProgress: 100,
    });

    if (api.bump) api.bump();

    return { ok: true };
  },

  /**
   * Update caption on an uploaded photo (server sync on blur)
   * @param {string} photoId - UUID of the photo
   * @param {string} caption - New caption text
   * @param {function|null} emitPatch - WebSocket emit (null for local-only onChange)
   * @param {string} formId - Parent form UUID
   * @param {string} formType - Type of form
   * @returns {Promise<{ok: boolean, error?: string}>|undefined}
   */
  updatePhotoCaption: async (photoId, caption, emitPatch, formId, formType) => {
    // Update local state
    const formData = api.getFormData();
    const photo = formData.photos?.find((p) => p.id === photoId);
    if (photo) {
      photo.updateCaption(caption);
      if (api.bump) api.bump();
    }

    // If emitPatch provided (onBlur), sync to server
    if (emitPatch && photo) {
      const result = await emitPatch({
        patch_type: "photos",
        [`${formType}_id`]: formId,
        photo_data: photo.toCaptionPatch(),
      });
      return {
        ok: result.success,
        error: result.error,
      };
    }
  },

  /**
   * Delete a photo from S3 and database
   * @param {string} photoId - UUID of the photo to delete
   * @param {function} emitPatch - WebSocket emit function
   * @param {string} formId - Parent form UUID
   * @param {string} formType - Type of form
   * @returns {Promise<{ok: boolean, error?: string}>}
   */
  deletePhoto: async (photoId, emitPatch, formId, formType) => {
    const formData = api.getFormData();
    const photo = formData.photos?.find((p) => p.id === photoId);
    if (!photo) {
      return { ok: false, error: "Photo not found" };
    }

    // Step 1: Remove from database and get delete URL
    const result = await emitPatch({
      patch_type: "photos",
      [`${formType}_id`]: formId,
      photo_data: photo.toRemovePatch(),
    });

    if (!result.success) {
      api.setPhotosUi({
        showDeleteConfirm: false,
        photoToDelete: null,
      });
      return { ok: false, error: result.error || "Failed to delete photo" };
    }

    // Step 2: Delete from S3 using presigned delete URL
    if (result.data?.delete_url) {
      await deleteFileFromS3(result.data.delete_url);
    }

    // Step 3: Remove from local state
    formData.photos = formData.photos.filter((p) => p.id !== photoId);

    api.setPhotosUi({
      showDeleteConfirm: false,
      photoToDelete: null,
    });

    if (api.bump) api.bump();

    return { ok: true };
  },

  // ============================================
  // UI STATE ACTIONS
  // ============================================

  /**
   * Show delete confirmation modal
   * @param {string} photoId - UUID of photo to potentially delete
   */
  showDeleteConfirm: (photoId) => {
    api.setPhotosUi({
      showDeleteConfirm: true,
      photoToDelete: photoId,
    });
  },

  /**
   * Hide delete confirmation modal
   */
  hideDeleteConfirm: () => {
    api.setPhotosUi({
      showDeleteConfirm: false,
      photoToDelete: null,
    });
  },

  /**
   * Clear upload error message
   */
  clearUploadError: () => {
    api.setPhotosUi({ uploadError: null });
  },

  /**
   * Open photo view/zoom modal
   * @param {FormPhotos} photo - Photo instance to view
   */
  openViewModal: (photo) => {
    api.setPhotosUi({
      viewModalOpen: true,
      viewingPhoto: photo,
    });
  },

  /**
   * Close photo view/zoom modal
   */
  closeViewModal: () => {
    api.setPhotosUi({
      viewModalOpen: false,
      viewingPhoto: null,
    });
  },

  /**
   * Download a photo using its presigned URL
   * @param {FormPhotos} photo - Photo instance to download
   * @returns {Promise<{ok: boolean, error?: string}>}
   */
  downloadPhoto: async (photo) => {
    const url = typeof photo.getDisplayUrl === "function"
      ? photo.getDisplayUrl()
      : (photo.localPreviewUrl || photo.presignedUrl || photo.presigned_url || null);
    if (!url) {
      return { ok: false, error: "No URL available for download" };
    }

    const filename = `photo_${photo.id}.${photo.mimeType?.split("/")[1] || "jpg"}`;
    return await downloadFileFromS3(url, filename);
  },

  // ============================================
  // BROADCAST HANDLER (for multi-tab sync)
  // ============================================

  /**
   * Handle incoming websocket broadcast for photos.
   * Applies add/update/remove operations from other tabs.
   *
   * @param {object} patchData - Broadcast data with patch_type, action, and photo data
   */
  handleBroadcast: (patchData) => {
    const formData = api.getFormData();
    if (!formData) return;

    const { action, id } = patchData;

    if (action === "add") {
      // Create new photo instance from broadcast data (includes geo fields)
      const newPhoto = new FormPhotos({
        id: patchData.id,
        s3_key: patchData.s3_key,
        caption: patchData.caption,
        mime_type: patchData.mime_type,
        size: patchData.size,
        width: patchData.width,
        height: patchData.height,
        presigned_url: patchData.presigned_url,
        latitude: patchData.latitude,
        longitude: patchData.longitude,
        altitude: patchData.altitude,
        bearing: patchData.bearing,
        accuracy: patchData.accuracy,
        altitude_accuracy: patchData.altitude_accuracy,
        address: patchData.address,
        captured_at: patchData.captured_at,
        captured_at_local: patchData.captured_at_local,
      });

      // Add to photos array
      if (!formData.photos) formData.photos = [];
      formData.photos.push(newPhoto);

      if (api.bump) api.bump();
      console.log("Photo added via broadcast:", id);
      return;
    }

    if (action === "remove") {
      // Remove photo from array
      if (formData.photos) {
        formData.photos = formData.photos.filter((p) => p.id !== id);
        if (api.bump) api.bump();
        console.log("Photo removed via broadcast:", id);
      }
      return;
    }

    if (action === "update") {
      // Update caption on existing photo
      const photo = formData.photos?.find((p) => p.id === id);
      if (photo) {
        photo.updateCaption(patchData.caption);
        if (api.bump) api.bump();
        console.log("Photo caption updated via broadcast:", id);
      }
      return;
    }
  },
});
